/**
 * CR424 + CR428: Unified hook for editor sample deals.
 * 
 * Two-step resolution:
 * 1. Calls get_deal_card_editor_sample_deal RPC to obtain deal_id + pointer meta
 * 2. Passes that deal_id through get_deal_card_render_payload for hardened derived fields
 * 
 * All editor tabs render from the render payload, ensuring computed_value_cad
 * and other derived fields are never missing due to inconsistent sources.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDealCardRenderPayload, type RenderPayloadMeta } from "./useDealCardRenderPayload";

// ================================================================
// TYPES
// ================================================================

export interface EditorSampleDealMeta {
  source: "pointer" | "auto" | "none";
  deal_id: string | null;
  dedupe_key: string | null;
  deal_type: string;
  environment: string;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  eligibility_requirements: Record<string, unknown> | null;
  updated_at: string | null;
  reason?: string;
  /** CR428: Render payload meta (computed_value_source, missing_fields, etc.) */
  renderPayload?: RenderPayloadMeta | null;
}

export interface EditorSampleDealResult {
  deal: Record<string, unknown> | null;
  meta: EditorSampleDealMeta | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ================================================================
// HOOK
// ================================================================

export function useDealCardEditorSampleDeal(
  environment: string = "staging",
  dealType: string | null = null
): EditorSampleDealResult {
  const [sampleMeta, setSampleMeta] = useState<EditorSampleDealMeta | null>(null);
  const [sampleDealId, setSampleDealId] = useState<string | null>(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);

  // Step 1: Get sample deal pointer (deal_id + source metadata)
  const fetchSamplePointer = useCallback(async () => {
    if (!dealType) {
      setSampleMeta(null);
      setSampleDealId(null);
      return;
    }

    setSampleLoading(true);
    setSampleError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc(
        "get_deal_card_editor_sample_deal" as any,
        {
          p_environment: environment,
          p_deal_type: dealType,
        }
      );

      if (rpcError) throw new Error(rpcError.message);

      const result = data as { deal_json: Record<string, unknown> | null; meta: EditorSampleDealMeta } | null;

      if (result?.meta) {
        setSampleMeta(result.meta);
        setSampleDealId(result.meta.deal_id);
      } else {
        setSampleMeta(null);
        setSampleDealId(null);
      }
    } catch (err) {
      console.error("[useDealCardEditorSampleDeal] Error:", err);
      setSampleError(err instanceof Error ? err.message : "Unknown error");
      setSampleMeta(null);
      setSampleDealId(null);
    } finally {
      setSampleLoading(false);
    }
  }, [environment, dealType]);

  useEffect(() => {
    fetchSamplePointer();
  }, [fetchSamplePointer]);

  // Step 2: Pass deal_id through render payload for hardened fields
  const renderPayload = useDealCardRenderPayload(environment, sampleDealId);

  // Merge meta: sample pointer meta + render payload meta
  const mergedMeta = useMemo<EditorSampleDealMeta | null>(() => {
    if (!sampleMeta) return null;
    return {
      ...sampleMeta,
      renderPayload: renderPayload.meta,
    };
  }, [sampleMeta, renderPayload.meta]);

  const loading = sampleLoading || renderPayload.loading;
  const error = sampleError || renderPayload.error;

  const refetch = useCallback(() => {
    fetchSamplePointer();
  }, [fetchSamplePointer]);

  return useMemo(() => ({
    deal: renderPayload.deal,
    meta: mergedMeta,
    loading,
    error,
    refetch,
  }), [renderPayload.deal, mergedMeta, loading, error, refetch]);
}
